import type { Article } from '../types'

export const article: Article = {
  meta: {
    slug: 'pobyt-s-kadi',
    title: 'Pobyt s kádí: horká lázeň pod širým nebem',
    perex:
      'Kád není vířivka bez trysek. Je to obřad: roztopíte kamna, počkáte, až se voda prohřeje, a pak sedíte venku v horké vodě, dokud nevystydne. Poradíme, jak si pobyt s kádí vybrat a na co se hostitele zeptat předem.',
    coverImage: 'https://images.unsplash.com/photo-1708630283392-678476d547f8?w=1200&q=80&auto=format&fit=crop',
    coverImageAlt: 'Dřevěná kád s kamny na zasněžené louce nad horským údolím',
    publishedAt: '2026-09-14',
    author: 'Redakce Mátevolno',
    tags: ['wellness', 'kád', 'podzim', 'relax'],
  },
  content: `Z komínku stoupá kouř, ve vodě se odráží nebe a vy sedíte po ramena ponoření venku v horké lázni. Kolem je ticho, protože kád nehučí – nemá čerpadlo ani trysky, jen dřevěná kamna a oheň, který jste rozdělali sami.

Právě tím se pobyt s kádí liší od běžného wellness. Nezmáčknete tlačítko a nepočkáte deset minut. Zatopíte a máte celé odpoledne důvod nikam nespěchat.

## Kád je dřevo, oheň a trpělivost

Kád je velká dřevěná nádrž, obvykle z modřínu nebo smrku, kterou ohřívají kamna na dřevo. U některých stojí kamna přímo ve vodě, u jiných vedle kádě a voda mezi nimi obíhá sama. V obou případech platí to samé: teplotu si řídíte přikládáním a voda se prohřívá hodiny, ne minuty.

Zní to jako práce navíc – a trochu ano. Jenže ta práce je součást pobytu. Nanosíte dřevo, zatopíte, mezitím se projdete po okolí a když se vrátíte, čeká na vás lázeň, kterou jste si udělali sami.

::nabidky{facility="hotTub" text="Prohlédnout pobyty s kádí"}

## Kád, nebo vířivka? Rozdíl není v teplotě vody

Obojí je horká voda venku, zážitek je ale jiný. Vířivka je pohodlná a okamžitá, kád pomalý a tichý. Pokud vás láká spíš to první, mrkněte na náš článek o tom, co nabízí [pobyt s vířivkou](/inspirace/pobyt-s-virivkou).

| Kád | Vířivka |
| --- | --- |
| Topí se dřevem, ohřev trvá hodiny | Nahřátá na stálou teplotu, hned k použití |
| Ticho, jen praskání dřeva | Šumění trysek a čerpadla |
| Koupel bez masáže, zato hlubší | Masážní trysky a bublinky |
| Teplotu ovlivníte přikládáním | Teplotu nastavíte na displeji |
| Sami si ji připravíte | Stačí odklopit víko |

![Dřevěná kád s komínem a připravenou hromadou dřeva na kraji lesa](https://images.unsplash.com/photo-1713706980185-26059d47ea87?w=1200&q=80&auto=format&fit=crop)

## Topení je půlka zážitku

Rozdělat oheň, přiložit a čas od času zkontrolovat, jak je voda teplá – to je rituál, který má svoje kouzlo hlavně ve dvou. Zatímco se voda ohřívá, dá se stihnout výlet, oběd i odpolední spánek.

Vyplatí se počítat s tím dopředu. Když dorazíte v pátek večer po práci, nejspíš se budete koupat až v sobotu odpoledne. Kdo chce mít kád hotovou hned po příjezdu, měl by se domluvit s hostitelem – někde zatopí předem.

## Koupel, kterou nemá smysl uspěchat

Několik zásad, ať vám horká voda jen prospěje:

- **Pijte během koupele.** V horké vodě se potíte víc, než si uvědomujete.
- **Střídejte teplo a chlad.** Vylezte, ochlaďte se na vzduchu nebo ve sprše a vraťte se. Tělo to zvládne líp než jedna dlouhá koupel v kuse.
- **Vynechte alkohol.** Horká voda a víno k sobě nepatří, oběma se zatěžuje srdce.
- **Hlídejte děti.** Pro nejmenší je horká lázeň náročná, do kádě vždy jen na chvilku a pod dohledem.
- **Nechte oheň dohořet.** Kamna sálají i po koupeli, k vodě proto po setmění pouštějte děti jen s dospělým.

Kdo si chce dopřát celou domácí lázeň, může kád spojit se saunou – v nabídce najdete i [chalupy se saunou](/pobyty/se-saunou), kde je obojí kousek od sebe.

## Na co se zeptat, než rezervujete

- **Je dřevo v ceně?** Někde je připravené u kádě, jinde si ho kupujete zvlášť.
- **Jak dlouho trvá ohřev?** Liší se podle velikosti kádě i počasí, hostitel to ví přesně.
- **Kdo kád napouští a vypouští?** U některých pobytů je to na hostech, u jiných na majiteli.
- **Je koupel součástí ceny pobytu?** Kád bývá zpoplatněná zvlášť, ptejte se dřív, než si na ni uděláte program.
- **Je u kádě soukromí?** Pro klid ve dvou je dobré vědět, jestli na ni není vidět od sousedů.

## Kam za kádí vyrazit

Kád potřebuje kus zahrady a klid kolem, takže ji najdete spíš u samot a chalup na kraji lesa než v centru obce. Hodně jich stojí v podhůří a v kopcovitých krajích, kde k horké vodě přibude ještě výhled do údolí.

Pěkně se to potkává třeba v [jižních Čechách](/pobyty/jizni-cechy/s-kadi), kde je rybníků a lesů dost na celý víkend výletů, ale funguje to stejně dobře v horách. Po dni na trase nebo na kole je večerní koupel to nejlepší, co můžete unaveným nohám dopřát.

## Nejlepší sezona začíná, když se ochladí

Kád funguje celý rok, ale naplno vynikne, až když je venku zima. Sedět v horké vodě, zatímco nad hladinou stoupá pára a vzduch štípe do tváří, je zážitek, na který se v létě nepřijde. Podzim a zima jsou pro kád ideální – a chalupy jsou v tuhle dobu volnější než v sezoně.

Prohlédněte si všechny [pobyty s kádí](/pobyty/s-kadi) a vyberte si ten svůj:

::cta{text="Prohlédnout všechny pobyty"}`,
}
